import { HTMLAttributes, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import styled from '@emotion/styled';
import { Button, message, Tooltip } from 'antd';
import { SyncOutlined } from '@ant-design/icons';
import StatusButton from 'components/StatusButton';
import { sync as syncApi } from 'services/api/review';
import { isFakeConversation, useReviewContext } from '../Context';

const Container = styled.header`
  flex: 0 0 48px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: var(--fett-bg-panel);
  overflow: hidden;
`;

const Left = styled.div`
  flex: 1 0 0;
  display: flex;
  align-items: center;
  margin-left: 16px;
  overflow: hidden;
`;

const Name = styled.div`
  flex: 1 0 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ButtonWrapper = styled.div`
  flex: 0 0 auto;
  > button {
    margin-right: 8px;
  }
`;

const Tag = styled.span`
  display: inline-block;
  margin: 0 16px 0 4px;
  padding: 0 4px;
  line-height: 18px;
  font-size: 12px;
`;

const RedTag = styled(Tag)`
  color: var(--fett-surface-error-text);
  background: #fae6e6;
`;

const BlueTag = styled(Tag)`
  color: var(--fett-scenario-accent-text);
  background: #d7e1fa;
`;

const GreenTag = styled(Tag)`
  color: var(--fett-scenario-success-text);
  background: #d7f5e8;
`;

const GreyTag = styled(Tag)`
  color: var(--fett-text-tertiary);
  background: var(--fett-bg-app);
`;

function DetailHeader(props: HTMLAttributes<HTMLHeadElement>) {
  const { conversation, profile, detail, refreshReview, updateStatus } = useReviewContext();

  const { commentId } = profile;

  const { handleName, status, channel } = detail;

  const hasConversationControlPermission =
    isFakeConversation(conversation) || conversation.controlStatus || conversation.supervisePower;

  const controlEnable = status <= 2 && hasConversationControlPermission;

  const { t } = useTranslate();

  const statusLabelMap = {
    1: <RedTag>{t('domain.review.status.pending')}</RedTag>,
    2: <BlueTag>{t('domain.review.status.inProgress')}</BlueTag>,
    3: <GreenTag>{t('domain.review.status.processed')}</GreenTag>,
    4: <GreyTag>{t('domain.review.status.noActionNeeded')}</GreyTag>,
  };

  const [syncing, setSyncing] = useState(false);

  const onSync = () => {
    setSyncing(true);

    return syncApi({
      commentId,
      channel,
    })
      .then(() => {
        return refreshReview();
      })
      .then(() => {
        setSyncing(false);

        message.success(t('scene.reviewWorkspace.detail.feedback.reviewUpdated'));
      })
      .catch(() => {
        setSyncing(false);

        message.error(t('scene.reviewWorkspace.detail.error.refreshFailed'));
      });
  };

  const syncButton = (
    <Button ghost type="primary" disabled={syncing} onClick={onSync}>
      <SyncOutlined spin={syncing} />
    </Button>
  );

  return (
    <Container {...props}>
      <Left>
        <div>
          {t('scene.reviewWorkspace.detail.field.status')}:{statusLabelMap[status]}
        </div>
        {handleName ? (
          <Name title={handleName}>
            {t('scene.reviewWorkspace.detail.field.assignee')}:{handleName}
          </Name>
        ) : null}
      </Left>
      <ButtonWrapper>
        {controlEnable && (
          <>
            <StatusButton onClick={() => updateStatus(4)}>
              {t('domain.review.status.noActionNeeded')}
            </StatusButton>
            <StatusButton type="primary" onClick={() => updateStatus(3)}>
              {t('domain.review.status.processed')}
            </StatusButton>
          </>
        )}
        {syncing ? (
          syncButton
        ) : (
          <Tooltip title={t('scene.reviewWorkspace.detail.action.refreshReview')}>
            {syncButton}
          </Tooltip>
        )}
      </ButtonWrapper>
    </Container>
  );
}

export default DetailHeader;
